/**
 * Inline SVG icon set — custom, thin-stroke, matching the editorial tone.
 *
 * All icons are 24×24 viewBox, 1.5px stroke, rounded caps+joins, and
 * inherit currentColor so we can tint with Tailwind.
 */

import type { SVGProps } from 'react';

type Props = SVGProps<SVGSVGElement> & { size?: number };

function base(size = 24) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
}

export const HomeIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1v-9.5Z" />
  </svg>
);

export const WalletIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a2 2 0 0 1 2 2v1.5" />
    <path d="M3 7.5V18a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3.5" />
    <path d="M21 9.5h-4a2.5 2.5 0 0 0 0 5h4Z" />
    <circle cx="17.5" cy="12" r=".8" fill="currentColor" stroke="none" />
  </svg>
);

export const ActivityIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M3 12h3.5L9 6l3.5 12L15 9l2 3h4" />
  </svg>
);

export const SendIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M5 12 20 4l-4 16-4-7-7-1Z" />
    <path d="m12 13 4-5" />
  </svg>
);

export const GiftIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M4 12v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8" />
    <path d="M12 8v13" />
    <path d="M12 8C10 8 7 7 7 5a2 2 0 0 1 5 0c0 2-3 3 0 3 3 0 0-1 5-1a2 2 0 0 0-5-2" />
  </svg>
);

export const SparkleIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2 2M16.4 16.4l2 2M5.6 18.4l2-2M16.4 7.6l2-2" />
    <circle cx="12" cy="12" r="3.5" />
  </svg>
);

export const ArrowRightIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const ArrowLeftIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
);

export const CopyIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V5a1 1 0 0 1 1-1h10" />
  </svg>
);

export const CheckIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="m5 12.5 4.5 4.5L20 7" />
  </svg>
);

export const CloseIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export const SettingsIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.8 1.8 0 0 0 .4 2l.1.1a2.2 2.2 0 0 1-1.5 3.7 2.2 2.2 0 0 1-1.6-.6l-.1-.1a1.8 1.8 0 0 0-2-.4 1.8 1.8 0 0 0-1.1 1.6v.2a2.2 2.2 0 1 1-4.4 0v-.1a1.8 1.8 0 0 0-1.2-1.7 1.8 1.8 0 0 0-2 .4l-.1.1a2.2 2.2 0 0 1-3.7-1.5 2.2 2.2 0 0 1 .6-1.6l.1-.1a1.8 1.8 0 0 0 .4-2 1.8 1.8 0 0 0-1.6-1.1h-.2a2.2 2.2 0 1 1 0-4.4h.1a1.8 1.8 0 0 0 1.7-1.2 1.8 1.8 0 0 0-.4-2l-.1-.1a2.2 2.2 0 1 1 3.1-3.1l.1.1a1.8 1.8 0 0 0 2 .4h.1a1.8 1.8 0 0 0 1.1-1.6v-.2a2.2 2.2 0 1 1 4.4 0v.1a1.8 1.8 0 0 0 1.1 1.6 1.8 1.8 0 0 0 2-.4l.1-.1a2.2 2.2 0 0 1 3.1 3.1l-.1.1a1.8 1.8 0 0 0-.4 2v.1a1.8 1.8 0 0 0 1.6 1.1h.2a2.2 2.2 0 1 1 0 4.4h-.1a1.8 1.8 0 0 0-1.6 1.1Z" />
  </svg>
);

export const ShareIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
    <path d="M16 6l-4-4-4 4" />
    <path d="M12 2v14" />
  </svg>
);

export const PlusIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const ShieldIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export const BookIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M4 4h11a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4Z" />
    <path d="M4 17a3 3 0 0 1 3-3h11" />
  </svg>
);

export const ChevronDownIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const QuestionIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7V14" />
    <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

export const BellIcon = ({ size, ...p }: Props) => (
  <svg {...base(size)} {...p}>
    <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2H4.5L6 16Z" />
    <path d="M10 20a2 2 0 0 0 4 0" />
  </svg>
);
